import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  apercuEnvoiDevis,
  deleteDevis,
  envoyerDevis,
  getDevis,
  getDevisById,
  regenererPdfDevis,
  telechargerPdfDevis,
  urlPdfDevis,
} from '../services/devisService';
import ApercuEmail from './ApercuEmail';
import ActionsLigne from '../components/ActionsLigne';
import GenererSuivant from '../components/GenererSuivant';
import ConfirmationSuppression from '../components/ConfirmationSuppression';
import '../assets/styles/Listes.css';
import { ajouterHistorique } from '../services/contactService';
import DevisPanel from './DevisPanel';
import { formatAnciennete, formatDate, formatMontant } from './devisUtils';
import FiltresRepliables from '../components/FiltresRepliables';
import '../assets/styles/DemandeDevis.css';
import ModeleEmailPanneau from './ModeleEmailPanneau';
import PanneauApercu from './PanneauApercu';
import BoutonExport from './BoutonExport';
import FiltreDossier from './FiltreDossier';
import StatutsBarre from './StatutsBarre';
import { FACETTES_DEVIS, FACETTES_ORIGINE } from './facettes';
import '../assets/styles/Devis.css';
import { FlashToast, toast } from '../components/Toasts';
import { IconeOeil, IconeTelecharger } from '../components/IconesDocument';

const LIMIT = 15;

const COLUMNS = [
  { key: 'numero', label: 'NUMÉRO' },
  { key: 'client', label: 'CLIENT' },
  { key: 'montant', label: 'MONTANT' },
  { key: 'statut', label: 'SUIVI' },
  { key: 'date', label: 'SANS RÉPONSE' },
];

/**
 * Libellés du suivi calculé par le serveur (cf. FACETTES_DEVIS). La page ne
 * liste que des devis partis : le statut brut du devis n'apprend plus rien,
 * c'est la réponse du client et l'échéance de relance qui comptent.
 */
const LIBELLE_SUIVI = {
  a_envoyer: 'À envoyer',
  envoye: 'Devis envoyé',
  a_relancer: 'Devis à relancer',
  relance: 'Devis relancé',
  accepte: 'Devis accepté et signé',
  deja_conclu: 'Déjà conclu',
  sans_suite: 'Sans suite',
};

function classePastille(devis) {
  if (devis.suivi === 'accepte' || devis.suivi === 'deja_conclu') return ' devis-pill-valide';
  // Validé mais jamais parti : même urgence qu'une relance due.
  if (devis.suivi === 'a_envoyer') return ' devis-pill-refuse';
  if (devis.suivi === 'a_relancer') return ' devis-pill-refuse';
  if (devis.suivi === 'relance') return ' devis-pill-attente';
  if (devis.statut === 'refuse') return ' devis-pill-refuse';
  return '';
}

/**
 * Ligne d'appoint sous la pastille. Pour un devis en attente de réponse, la
 * date de validation n'apprend plus rien : ce qui compte est depuis combien
 * de temps le client n'a pas répondu, pour décider d'une relance.
 */
function detailStatut(d) {
  // Rien à dire d'une ancienneté d'envoi quand l'envoi n'a pas eu lieu.
  if (d.suivi === 'a_envoyer') {
    return `validé ${formatAnciennete(d.valide_le)} · l'email n'est jamais parti`;
  }
  if (d.suivi === 'accepte') return `accepté le ${formatDate(d.date_reponse_client)}`;
  if (d.suivi === 'deja_conclu') return 'conclu ailleurs · repris tel quel, jamais envoyé d’ici';
  if (d.suivi === 'sans_suite') return 'demande close';
  if (d.statut === 'refuse') return `refusé le ${formatDate(d.date_reponse_client)}`;

  const nb = Number(d.nb_relances) || 0;
  const depuis = formatAnciennete(d.date_derniere_relance || d.date_envoi);
  return `${nb > 0 ? `relancé ${depuis} · ${nb} relance${nb > 1 ? 's' : ''}` : `envoyé ${depuis}`}`;
}

function Devis() {
  const [devis, setDevis] = useState([]);
  // « Régénérer le PDF » depuis l'aperçu : le cadre se recharge (paramètre v).
  const [regenId, setRegenId] = useState(null);
  const [versionApercu, setVersionApercu] = useState(0);

  async function regenererPdf(d) {
    setRegenId(d.id);
    try {
      const res = await regenererPdfDevis(d.id);
      setVersionApercu((v) => v + 1);
      toast.succes(res.message || 'PDF refait.');
    } catch (err) {
      toast.erreur(err.message || 'Régénération impossible.');
    } finally {
      setRegenId(null);
    }
  }

  // Réglage de l'email propre à cette page (cf. ModeleEmailPanneau).
  const [panneauEmail, setPanneauEmail] = useState(false);
  const [total, setTotal] = useState(0);
  const [compteurs, setCompteurs] = useState({});
  const [compteursOrigine, setCompteursOrigine] = useState({});
  // Interne (généré ici) ou externe (établi ailleurs, puis téléversé).
  const [origine, setOrigine] = useState('');
  const [page, setPage] = useState(1);

  // Dossier d'où l'on vient (boutons d'accès d'une demande, cf. Avancement) :
  // `cibleContact` peint ses lignes en vert et ne bouge plus, `filtreContact`
  // restreint la liste et se lève depuis le bandeau.
  const [params] = useSearchParams();
  const cibleContact = Number(params.get('contact')) || null;
  const cibleNom = params.get('client') || '';
  const [filtreContact, setFiltreContact] = useState(cibleContact);

  useEffect(() => {
    setFiltreContact(cibleContact);
  }, [cibleContact]);

  const [search, setSearch] = useState('');
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('DESC');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);

  // Ouverture d'un devis : la liste ne porte que l'entête, le panneau a besoin
  // des lignes et des modalités.
  const [ouvertureId, setOuvertureId] = useState(null);
  const [devisOuvert, setDevisOuvert] = useState(null);
  // Ouvert depuis le crayon : le panneau démarre sur le formulaire.
  const [ouvrirEnEdition, setOuvrirEnEdition] = useState(false);
  const [suppression, setSuppression] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [ligneErreur, setLigneErreur] = useState(null);
  const [pdfId, setPdfId] = useState(null);
  // Aperçu du devis : le document tel qu’il part, sans quitter la liste.
  const [apercuOuvert, setApercuOuvert] = useState(null);
  const [relanceId, setRelanceId] = useState(null);
  const [abandonId, setAbandonId] = useState(null);
  const [flash, setFlash] = useState(null);

  // La recherche part au serveur : on attend une pause de frappe pour ne pas
  // déclencher une requête par caractère.
  useEffect(() => {
    const timer = setTimeout(() => {
      setRecherche(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let annule = false;

    setLoading(true);
    setError(null);

    getDevis({
      page,
      limit: LIMIT,
      search: recherche,
      statut,
      origine,
      contactId: filtreContact,
      dateDebut,
      dateFin,
      sortBy,
      sortDir,
    })
      .then((res) => {
        if (annule) return;
        setDevis(res.data || []);
        setTotal(res.pagination?.total ?? (res.data || []).length);
        setCompteurs(res.compteurs || {});
        setCompteursOrigine(res.compteursOrigine || {});
      })
      .catch(() => {
        if (!annule) setError('Impossible de charger les devis.');
      })
      .finally(() => {
        if (!annule) setLoading(false);
      });

    return () => {
      annule = true;
    };
  }, [page, recherche, statut, origine, filtreContact, dateDebut, dateFin, sortBy, sortDir, reload]);

  async function handleOuvrir(id, enEdition = false) {
    setOuvertureId(id);
    setLigneErreur(null);

    try {
      const res = await getDevisById(id);
      setOuvrirEnEdition(enEdition);
      setDevisOuvert(res.data);
    } catch (err) {
      setLigneErreur({ id, message: err.message || 'Ouverture du devis impossible.' });
    } finally {
      setOuvertureId(null);
    }
  }

  async function handleSupprimer() {
    if (!suppression) return;
    const cible = suppression;
    setSuppressionEnCours(true);
    try {
      const res = await deleteDevis(cible.id);
      const b = res.data || {};
      setFlash(
        `Devis ${cible.numero} supprimé` +
          (b.contrats ? `, avec son contrat et ${b.journees} journée(s)` : '') +
          '. La demande repasse à traiter.'
      );
      setSuppression(null);
      setReload((n) => n + 1);
    } catch (err) {
      setLigneErreur({ id: cible.id, message: err.message || 'Suppression impossible.' });
      setSuppression(null);
    } finally {
      setSuppressionEnCours(false);
    }
  }

  async function handlePdf(id) {
    setPdfId(id);
    setLigneErreur(null);

    try {
      await telechargerPdfDevis(id);
      // Le téléchargement renseigne pdf_path : la ligne doit refléter la pièce.
      setReload((n) => n + 1);
    } catch (err) {
      setLigneErreur({ id, message: err.message || 'Téléchargement impossible.' });
    } finally {
      setPdfId(null);
    }
  }

  // L'aperçu de l'email avant l'envoi : le devis dont la relance est en
  // train de se relire, ou null.
  const [apercuRelance, setApercuRelance] = useState(null);

  /** Ouvre l'aperçu de l'email ; rien ne part avant le clic dans la modale. */
  function relancer(d) {
    setLigneErreur(null);
    setFlash(null);
    setApercuRelance(d);
  }

  /** L'envoi réel, depuis la modale d'aperçu (texte éventuellement corrigé). */
  async function envoyerRelance(d, { sujet, texte }) {
    setRelanceId(d.id);
    try {
      const res = await envoyerDevis(d.id, { sujet, texte });
      const dest = res.data.client_email || res.data.contact_email;
      setFlash(`Relance du devis ${d.numero} envoyée à ${dest}.`);
      setReload((n) => n + 1);
    } finally {
      setRelanceId(null);
    }
  }

  /**
   * Clore la demande sans suite. Le devis n'est pas modifié : on écrit une
   * entrée d'historique, et c'est elle qui devient le statut de la demande.
   */
  async function classerSansSuite(d) {
    setAbandonId(d.id);
    setLigneErreur(null);
    setFlash(null);
    try {
      await ajouterHistorique(d.contact_id, {
        statut: 'sans_suite',
        commentaire: `Devis ${d.numero} classé sans suite depuis la liste des devis.`,
      });
      setFlash(`Demande de ${d.client_societe} classée sans suite.`);
      setReload((n) => n + 1);
    } catch (err) {
      setLigneErreur({ id: d.id, message: err.message || 'Enregistrement impossible.' });
    } finally {
      setAbandonId(null);
    }
  }

  function handleSort(key) {
    if (sortBy === key) {
      setSortDir((dir) => (dir === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(key);
      setSortDir('ASC');
    }
    setPage(1);
  }

  function handleFiltre(setter) {
    return (e) => {
      setter(e.target.value);
      setPage(1);
    };
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const rangeStart = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const rangeEnd = Math.min(page * LIMIT, total);
  const filtreActif = Boolean(statut || origine || dateDebut || dateFin || recherche.trim());

  return (
    <div className="devis-page">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Devis</h1>
        </div>
        <div className="devis-header-actions">
          <BoutonExport
            chemin="/devis/export"
            filtres={{
              statut,
              origine,
              search: recherche,
              contactId: filtreContact,
              dateDebut,
              dateFin,
              sortBy,
              sortDir,
            }}
          />
          <button
            type="button"
            className="devis-btn"
            onClick={() => setPanneauEmail(true)}
            title="Objet et contenu de l'email envoyé au client depuis cette page"
          >
            Paramétrage email
          </button>
        </div>
      </div>

      <FiltreDossier
        nom={cibleNom}
        actif={Boolean(filtreContact)}
        total={total}
        onVoirTout={() => {
          setFiltreContact(null);
          setPage(1);
        }}
      />

      <FiltresRepliables libelle="Filtres">
        <div className="devis-toolbar">
          <input
            type="text"
            className="devis-search"
            placeholder="Rechercher un numéro, un client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="devis-date-filter">
            <div className="devis-periode">
              <label className="devis-date-field">
                <span>Du</span>
                <input
                  type="date"
                  value={dateDebut}
                  onChange={handleFiltre(setDateDebut)}
                  max={dateFin || undefined}
                />
              </label>
              <label className="devis-date-field">
                <span>Au</span>
                <input
                  type="date"
                  value={dateFin}
                  onChange={handleFiltre(setDateFin)}
                  min={dateDebut || undefined}
                />
              </label>
            </div>
            {filtreActif && (
              <button
                type="button"
                className="devis-date-clear"
                onClick={() => {
                  setSearch('');
                  setStatut('');
                  setOrigine('');
                  setDateDebut('');
                  setDateFin('');
                  setPage(1);
                }}
              >
                Réinitialiser
              </button>
            )}
          </div>
          <span className="devis-total">{total} devis</span>
        </div>

        <FlashToast texte={flash} onFermer={() => setFlash(null)} />

        <StatutsBarre
          titre="Suivi"
          options={FACETTES_DEVIS}
          compteurs={compteurs}
          valeur={statut}
          total={Object.values(compteurs).reduce((a, n) => a + (Number(n) || 0), 0)}
          libelleTous="Tous"
          onSelect={(v) => {
            setStatut(v);
            setPage(1);
          }}
        />
        {/* Interne : généré par l'application ; externe : établi hors de
            l'application puis téléversé. Se combine avec les autres pastilles. */}
        <StatutsBarre
          titre="Origine"
          options={FACETTES_ORIGINE}
          compteurs={compteursOrigine}
          valeur={origine}
          total={Object.values(compteursOrigine).reduce((a, n) => a + (Number(n) || 0), 0)}
          libelleTous="Toutes"
          onSelect={(v) => {
            setOrigine(v);
            setPage(1);
          }}
        />
      </FiltresRepliables>


      <div className="devis-card">

        <div className="devis-table-wrap">
          {loading && <div className="devis-state">Chargement des devis...</div>}

          {!loading && error && <div className="devis-state devis-state-error">{error}</div>}

          {!loading && !error && devis.length === 0 && (
            <div className="devis-empty">
              <div className="devis-empty-title">Aucun devis ne correspond à cette vue.</div>
              <div className="devis-empty-subtitle">
                Les devis générés depuis les demandes apparaîtront ici.
              </div>
            </div>
          )}

          {!loading && !error && devis.length > 0 && (
            <table className="devis-table devis-table-suivi">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="devis-th-sortable" onClick={() => handleSort(col.key)}>
                      {col.label}
                      <span
                        className={`devis-sort-icon${sortBy === col.key ? ' devis-sort-icon-active' : ''}`}
                      >
                        {sortBy === col.key ? (sortDir === 'ASC' ? '↑' : '↓') : '↕'}
                      </span>
                    </th>
                  ))}
                  <th className="devis-th-actions">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {devis.map((d) => (
                  <tr
                    key={d.id}
                    className={
                      'devis-row' + (d.contact_id === cibleContact ? ' devis-row-cible' : '')
                    }
                    onClick={() => setApercuOuvert(d)}
                  >
                    <td>
                      <div className="devis-numero">
                        {d.numero}
                        {d.origine === 'externe' && (
                          <span
                            className="badge-origine badge-origine-externe"
                            title="Devis établi hors de l’application, puis téléversé"
                          >
                            Externe
                          </span>
                        )}
                      </div>
                      <div className="devis-contact-email">
                        {d.nb_lignes} ligne{d.nb_lignes > 1 ? 's' : ''}
                        {d.pdf_path ? ' · PDF disponible' : ''}
                      </div>
                    </td>
                    <td>
                      <div className="devis-contact-name">{d.client_societe}</div>
                      <div className="devis-contact-email">
                        {d.prenom} {d.nom} · {d.contact_email}
                      </div>
                    </td>
                    <td>
                      <div className="devis-montant-ht">{formatMontant(d.montant_ht)} HT</div>
                      <div className="devis-contact-email">{formatMontant(d.montant_ttc)} TTC</div>
                    </td>
                    <td>
                      <span className={`devis-status-pill${classePastille(d)}`}>
                        {LIBELLE_SUIVI[d.suivi] || 'Devis envoyé'}
                      </span>
                      <div className="devis-status-detail">{detailStatut(d)}</div>
                    </td>
                    <td>
                      {d.date_reponse_client ||
                      d.suivi === 'sans_suite' ||
                      d.suivi === 'a_envoyer' ? (
                        <span className="devis-status-detail">—</span>
                      ) : (
                        <>
                          <span
                            className={`devis-status-pill${
                              d.suivi === 'a_relancer' ? ' devis-pill-refuse' : ' devis-pill-attente'
                            }`}
                          >
                            {/* « 12 j » et non « 12 jours » : la pastille tient alors
                                dans une colonne étroite, sans revenir à la ligne. */}
                            {d.jours_sans_reponse} j
                          </span>
                          {d.validite_depassee && (
                            <div className="devis-status-detail">validité échue</div>
                          )}
                        </>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {/* La relecture tient la largeur de la pile ; relance et
                          classement se répondent sur une rangée, Voir et PDF
                          sur la suivante. */}
                      <div className="devis-actions-pile">
                        <button
                          type="button"
                          className={`devis-btn devis-btn-sm devis-action-large ${
                            d.valide_le ? 'devis-btn-dossier' : 'devis-btn-primary'
                          }`}
                          onClick={() => handleOuvrir(d.id)}
                          disabled={ouvertureId === d.id}
                        >
                          {ouvertureId === d.id
                            ? 'Ouverture'
                            : d.valide_le
                              ? 'Consulter'
                              : 'Valider'}
                        </button>
                        {/* Relance et classement sans suite ne concernent que
                            les dossiers encore en attente d'une réponse. */}
                        {!d.date_reponse_client && d.suivi !== 'sans_suite' && (
                          <>
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-primary"
                              onClick={() => relancer(d)}
                              disabled={relanceId === d.id || abandonId === d.id}
                            >
                              {relanceId === d.id
                                ? 'Envoi'
                                : d.suivi === 'a_envoyer'
                                  ? 'Envoyer'
                                  : 'Relancer'}
                            </button>
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-danger"
                              onClick={() => classerSansSuite(d)}
                              disabled={abandonId === d.id || relanceId === d.id}
                              title="Clore la demande : le devis n'est plus à relancer"
                            >
                              {abandonId === d.id ? 'En cours' : 'Sans suite'}
                            </button>
                          </>
                        )}
                        <GenererSuivant
                          devisId={d.id}
                          contratId={d.contrat_id}
                          parDefaut={d.contrat_id ? 'convocation' : 'contrat'}
                          onGenere={() => setReload((n) => n + 1)}
                        />
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm devis-btn-fichier devis-action-large"
                          onClick={() => setApercuOuvert(d)}
                          title="Voir le PDF du devis"
                        >
                          <IconeOeil />
                          Voir PDF devis
                        </button>
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm devis-btn-fichier devis-action-large"
                          onClick={() => handlePdf(d.id)}
                          disabled={pdfId === d.id}
                          title="Télécharger le PDF du devis"
                        >
                          {pdfId === d.id ? 'En cours' : (<><IconeTelecharger />Télécharger PDF devis</>)}
                        </button>
                        <ActionsLigne
                          onModifier={() => handleOuvrir(d.id, true)}
                          modifierTitre={
                            d.valide_le
                              ? 'Devis validé : il est figé, le PDF fait foi'
                              : 'Modifier le devis'
                          }
                          modifierDisabled={Boolean(d.valide_le) || ouvertureId === d.id}
                          onSupprimer={() => setSuppression(d)}
                          supprimerTitre="Supprimer le devis (et son contrat s’il en a un)"
                          supprimerDisabled={suppressionEnCours}
                        />
                      </div>
                      {ligneErreur?.id === d.id && (
                        <div className="devis-row-erreur">{ligneErreur.message}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && !error && total > 0 && (
          <div className="devis-pagination">
            <span className="devis-pagination-info">
              {rangeStart}-{rangeEnd} sur {total}
            </span>
            <div className="devis-pagination-controls">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Précédent
              </button>
              <span className="devis-pagination-page">
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {suppression && (
        <ConfirmationSuppression
          titre="Supprimer ce devis ?"
          sousTitre={`${suppression.numero} · ${suppression.societe_nom || suppression.client_societe || ''}`}
          details={[
            'Le devis, ses lignes et son PDF.',
          ]}
          note="Le contrat qui en découle est CONSERVÉ (journées, documents, émargements) : il est simplement détaché du devis. La demande et la fiche client restent aussi."
          enCours={suppressionEnCours}
          onAnnuler={() => setSuppression(null)}
          onConfirmer={handleSupprimer}
        />
      )}

      {devisOuvert && (
        <DevisPanel
          devis={devisOuvert}
          editionInitiale={ouvrirEnEdition}
          onClose={() => setDevisOuvert(null)}
          onDevisChange={(maj) => {
            setDevisOuvert(maj);
            setReload((n) => n + 1);
          }}
          onValide={() => {
            setDevisOuvert(null);
            setReload((n) => n + 1);
          }}
        />
      )}

      {apercuOuvert && (
        <PanneauApercu
          titre={apercuOuvert.numero}
          sousTitre={`${apercuOuvert.client_societe} · ${apercuOuvert.prenom} ${apercuOuvert.nom}`}
          url={`${urlPdfDevis(apercuOuvert.id, { apercu: true })}&v=${versionApercu}`}
          onClose={() => setApercuOuvert(null)}
          actions={
            <>
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={() => regenererPdf(apercuOuvert)}
                disabled={regenId === apercuOuvert.id || apercuOuvert.origine === 'externe'}
                title={
                  apercuOuvert.origine === 'externe'
                    ? 'Devis établi hors de l’application : son fichier fait foi, il ne se régénère pas'
                    : 'Refaire le PDF à partir des données actuelles du devis'
                }
              >
                {regenId === apercuOuvert.id ? 'Régénération' : 'Régénérer le PDF'}
              </button>
              <button
                type="button"
                className="devis-btn devis-btn-sm devis-btn-fichier"
                onClick={() => handlePdf(apercuOuvert.id)}
                disabled={pdfId === apercuOuvert.id}
              >
                {pdfId === apercuOuvert.id ? 'En cours' : (<><IconeTelecharger />Télécharger PDF devis</>)}
              </button>
            </>
          }
        />
      )}

      {panneauEmail && (
        <ModeleEmailPanneau type={'devis_relance'} onClose={() => setPanneauEmail(false)} />
      )}

      {apercuRelance && (
        <ApercuEmail
          titre={`Envoyer le devis ${apercuRelance.numero}`}
          sousTitre={apercuRelance.client_societe}
          charger={() => apercuEnvoiDevis(apercuRelance.id)}
          onEnvoyer={(edits) => envoyerRelance(apercuRelance, edits)}
          onClose={() => setApercuRelance(null)}
        />
      )}
    </div>
  );
}

export default Devis;
